import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { documentStoreService } from '@/services/document-store.service';
import { storageService } from '@/services/storage.service';
import {
  DocumentUrlInput,
  DocumentProgressStatusEnum,
} from '@/types/document-store.types';
import { UploadProgress } from '@/types/storage.types';
import toast from 'react-hot-toast';
import { getFileTypeInfo } from '@/utils/fileTypeIconUtils';

interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeId: string;
  storeName: string;
  onSuccess: () => void;
}

interface FileUpload {
  file: File;
  name: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  url?: string;
  key?: string;
  error?: string;
}

export default function UploadDocumentModal({
  isOpen,
  onClose,
  storeId,
  storeName,
  onSuccess,
}: UploadDocumentModalProps) {
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) return;

    const newFiles: FileUpload[] = Array.from(selectedFiles).map((file) => ({
      file,
      name: file.name,
      status: 'pending',
      progress: 0,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles) return;

    const newFiles: FileUpload[] = Array.from(droppedFiles).map((file) => ({
      file,
      name: file.name,
      status: 'pending',
      progress: 0,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return getFileTypeInfo(extension).icon;
  };

  const uploadFiles = async () => {
    if (files.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setIsUploading(true);

    try {
      const documentsToAdd: DocumentUrlInput[] = [];
      const failedUploads: string[] = [];
      let uploadErrorMessage = ''; // first real upload failure reason (e.g. out-of-credits 402)

      for (let i = 0; i < files.length; i++) {
        const fileUpload = files[i];

        // Already on the server. Without this, pressing Upload again after a partial failure
        // re-uploaded every successful file and added it to the store a second time.
        if (fileUpload.status === 'success') {
          continue;
        }

        try {
          // Validate file before upload
          const validation = storageService.validateFile(fileUpload.file);
          if (!validation.valid) {
            throw new Error(validation.error);
          }

          // Update status to uploading
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === i ? { ...f, status: 'uploading', progress: 0 } : f,
            ),
          );

          // Upload file to storage with progress tracking
          const uploadResponse = await storageService.uploadFile(
            fileUpload.file,
            {
              onProgress: (progress: UploadProgress) => {
                setFiles((prev) =>
                  prev.map((f, idx) =>
                    idx === i ? { ...f, progress: progress.percentage } : f,
                  ),
                );
              },
              metadata: {
                storeId,
                storeName,
              },
            },
          );

          // Use the actual URL and key from storage response
          const fileUrl = uploadResponse.url || uploadResponse.file_path || '';
          const fileKey =
            uploadResponse.key ||
            uploadResponse.file_path ||
            `documents/${storeId}/${fileUpload.name}`;

          // Use the fileType exactly as returned by storage API (MIME type)
          // The API expects the MIME type (e.g., "application/pdf"), not the extension
          const fileType =
            uploadResponse.fileType ||
            fileUpload.file.type ||
            'application/octet-stream';

          // Add document entry for the store
          documentsToAdd.push({
            name: uploadResponse.name || fileUpload.name, // Use name from storage response if available
            key: fileKey,
            url: fileUrl,
            fileType: fileType, // Send MIME type as-is to the API
            status: DocumentProgressStatusEnum.IN_PROGRESS,
            useAsExample: false,
            fileSize: uploadResponse.fileSize || fileUpload.file.size || 0,
          });

          // Update status to success
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === i
                ? {
                    ...f,
                    status: 'success',
                    progress: 100,
                    url: fileUrl,
                    key: fileKey,
                  }
                : f,
            ),
          );
        } catch (uploadError: any) {
          console.error(`Failed to upload ${fileUpload.name}:`, uploadError);
          failedUploads.push(fileUpload.name);

          const reason =
            uploadError?.response?.data?.error?.error_description ||
            uploadError?.response?.data?.message ||
            uploadError?.message ||
            'Upload failed';
          if (!uploadErrorMessage) uploadErrorMessage = reason;

          // Update status to error
          setFiles((prev) =>
            prev.map((f, idx) =>
              idx === i
                ? {
                    ...f,
                    status: 'error',
                    error: reason,
                  }
                : f,
            ),
          );
        }
      }

      // Add all successfully uploaded documents to the store
      if (documentsToAdd.length > 0) {
        await documentStoreService.batchAddDocuments(storeId, documentsToAdd);

        if (failedUploads.length > 0) {
          toast.error(
            `Failed to upload ${
              failedUploads.length
            } file(s): ${failedUploads.join(', ')}`,
          );
          toast.success(
            `Successfully uploaded ${documentsToAdd.length} document(s)`,
          );
        } else {
          toast.success(
            `Successfully uploaded ${documentsToAdd.length} document(s)`,
          );
        }

        // Refresh the store's document list whenever documents were actually added, not only when
        // EVERY file succeeded. onSuccess() used to sit inside the all-succeeded branch, so a batch
        // with one failure left the parent list stale: the successfully uploaded document existed on
        // the server but did not appear until the page was reloaded.
        onSuccess();

        // Close only on a clean run. With a failure the modal stays open so the failed file can be
        // removed or retried, which is why the refresh above had to be separated from the close.
        if (failedUploads.length === 0) {
          setTimeout(() => {
            handleClose();
          }, 1500);
        }
      } else {
        toast.error(
          uploadErrorMessage ||
            'All uploads failed. Please check your files and try again.',
        );
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(
        error?.response?.data?.error?.error_description ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to upload documents. Please try again.',
      );

      // Mark all pending/uploading files as error
      setFiles((prev) =>
        prev.map((f) =>
          f.status === 'pending' || f.status === 'uploading'
            ? { ...f, status: 'error', error: 'Upload failed' }
            : f,
        ),
      );
    } finally {
      setIsUploading(false);
    }
  };

  // How many files a press of Upload will actually send. Counts everything except the ones already on
  // the server, so a partial failure no longer offers to "Upload 2 Files" when only one is left to
  // send — and the button disables once nothing is outstanding instead of re-uploading.
  const pendingCount = files.filter((f) => f.status !== 'success').length;

  const handleClose = () => {
    if (isUploading) {
      toast.error('Please wait for upload to complete');
      return;
    }
    setFiles([]);
    onClose();
  };

  const getStatusIcon = (status: FileUpload['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'uploading':
        return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 common-modal-overlay">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Upload Documents</h2>
              <p className="text-sm text-gray-600 mt-1">
                Add documents to {storeName}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded-full hover:bg-gray-100"
              disabled={isUploading}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors mb-6"
          >
            <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900 mb-2">
              Drop files here or click to browse
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Supports Documents (PDF, DOCX, DOC, PPTX, TXT, CSV), Images (JPG,
              PNG, GIF, WEBP), Video (MP4, AVI, MOV), and Audio (MP3, WAV, M4A)
            </p>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept=".pdf,.docx,.doc,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.mp3,.wav,.m4a"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="btn btn-primary"
              disabled={isUploading}
            >
              Select Files
            </button>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Selected Files ({files.length})
              </h3>
              {files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex items-center gap-2">
                      {getFileIcon(file.name)}
                      {file.status === 'uploading' && (
                        <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                      )}
                      {file.status === 'success' && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                      {file.status === 'error' && (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {storageService.formatFileSize(file.file.size)}
                      </p>
                    </div>
                  </div>

                  {/* Progress or Status */}
                  <div className="flex items-center gap-3">
                    {file.status === 'uploading' && (
                      <div className="w-24">
                        <div className="bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-500 h-2 rounded-full transition-all"
                            style={{ width: `${file.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    {file.status === 'success' && (
                      <span className="text-xs text-green-600 font-medium">
                        Uploaded
                      </span>
                    )}
                    {file.status === 'error' && (
                      <span className="text-xs text-red-600 font-medium">
                        {file.error}
                      </span>
                    )}
                    {/* Removable while it is not in flight. 'error' matters most: a rejected file —
                        over the size limit, wrong type — was previously stuck in the list with no way
                        to discard it, so the only escape was cancelling the whole dialog. */}
                    {(file.status === 'pending' || file.status === 'error') && !isUploading && (
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        title="Remove this file"
                        aria-label={`Remove ${file.name}`}
                        className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-gray-50 rounded-b-lg flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {files.filter((f) => f.status === 'success').length} of{' '}
            {files.length} uploaded
          </p>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={uploadFiles}
              className="btn btn-primary"
              disabled={isUploading || pendingCount === 0}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                `Upload ${pendingCount} File${pendingCount !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
